import { LEGACY_MANUAL_PAYMENT_LABEL } from '@pos/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Suspense, useEffect, useMemo, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { BarCompare, DonutShare } from '../../components/charts';
import { ExportMenu } from '../../components/ExportMenu';
import { KpiCard } from '../../components/KpiCard';
import { Select } from '../../components/Select';
import { DataTable } from '../../components/Table';
import {
  getDailyAnalytics,
  getDailySales,
  listBranches,
} from '../../data/fetchers';
import { CHART_H } from '../../lib/chartTokens';
import type { ExportCol } from '../../lib/export';
import {
  fmtAmount,
  fmtBaht,
  fmtBahtShort,
  fmtInt,
  fmtPct,
  fmtSatangCompact,
} from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';
import { useDateRange } from '../../lib/useDateRange';
import type { SalesPageQuery, SaleTxn } from '../../types/domain';
import { ReportDateFilter } from './ReportDateFilter';
import { ReportHeader } from './ReportHeader';

const deltaFrac = (cur: number, prev: number): number =>
  prev > 0 ? (cur - prev) / prev : cur > 0 ? 1 : 0;
const chartSkeleton = (
  <div
    className="animate-pulse bg-bg-warm rounded-card"
    style={{ height: CHART_H.md }}
  />
);

// --- per-day breakdown table ---
interface DayRow {
  day: string;
  revenue: number;
  dod: number | null;
  share: number;
}
interface DayExportRow {
  day: string;
  revenueBaht: string;
  revenueSatang: number;
  dodPct: string;
  sharePct: string;
}
type MethodFilter =
  'all' | 'cash' | 'qr_gateway' | 'qr_personal' | 'reference_manual';
const paymentMethodLabel = (method: string): string => {
  if (method === 'cash') return 'เงินสด';
  if (method === 'qr_gateway' || method === 'qr_scb') return 'QR นิติ-SCB';
  if (method === 'qr' || method === 'qr_personal') return 'พร้อมเพย์ส่วนบุคคล';
  if (method === 'reference_manual') return LEGACY_MANUAL_PAYMENT_LABEL;
  return method;
};
const normalizePaymentMethod = (
  method: string,
): Exclude<MethodFilter, 'all'> => {
  if (
    method === 'cash' ||
    method === 'qr_personal' ||
    method === 'reference_manual'
  )
    return method;
  if (method === 'qr_gateway' || method === 'qr_scb') return 'qr_gateway';
  if (method === 'qr') return 'qr_personal';
  return 'reference_manual';
};
const effectivePaymentLabels = (t: SaleTxn): string[] => {
  const labels = (t.payments ?? [])
    .filter(
      (payment) =>
        payment.status === undefined ||
        payment.status === 'captured' ||
        payment.status === 'confirmed',
    )
    .map((payment) =>
      normalizePaymentMethod(payment.method) === 'reference_manual' &&
      payment.paymentLabel?.trim()
        ? payment.paymentLabel.trim()
        : paymentMethodLabel(payment.method),
    );
  return labels.length > 0 ? labels : [paymentMethodLabel(t.method)];
};
const DodCell = ({ v }: { v: number | null }) => {
  if (v == null) return <span className="text-ink-4">—</span>;
  const cls = v > 0.001 ? 'text-ok' : v < -0.001 ? 'text-danger' : 'text-ink-4';
  return (
    <span className={`num font-bold ${cls}`}>
      {v > 0 ? '▲ +' : v < 0 ? '▼ ' : '– '}
      {Math.abs(v * 100).toFixed(1)}%
    </span>
  );
};
const dayColumns: ColumnDef<DayRow, unknown>[] = [
  { accessorKey: 'day', header: 'วันที่' },
  {
    accessorKey: 'revenue',
    header: 'ยอดขาย',
    cell: (c) => <span className="num">{fmtAmount(c.getValue<number>())}</span>,
  },
  {
    accessorKey: 'dod',
    header: 'Δ วันก่อนหน้า',
    cell: (c) => <DodCell v={c.getValue<number | null>()} />,
  },
  {
    accessorKey: 'share',
    header: 'สัดส่วนช่วงนี้',
    cell: (c) => (
      <span className="num">{fmtPct(c.getValue<number>() * 100)}</span>
    ),
  },
];
const dayExportCols: ExportCol<DayExportRow>[] = [
  { key: 'day', header: 'วันที่' },
  { key: 'revenueBaht', header: 'ยอดขาย' },
  { key: 'revenueSatang', header: 'ยอดขาย (satang)' },
  { key: 'dodPct', header: 'Δ วันก่อนหน้า' },
  { key: 'sharePct', header: 'สัดส่วนช่วงนี้' },
];

// --- transaction sample table (real txns) ---
interface SalesRow {
  id: string;
  branchId: string;
  receiptNo: string;
  date: string;
  items: number;
  method: string;
  status: string;
  total: number;
}
const txnColumns: ColumnDef<SalesRow, unknown>[] = [
  { accessorKey: 'receiptNo', header: 'เลขบิล' },
  {
    accessorKey: 'date',
    header: 'วันที่',
    meta: { className: 'whitespace-nowrap tabular-nums' },
  },
  {
    accessorKey: 'items',
    header: 'รายการ',
    cell: (c) => <span className="num">{fmtInt(c.getValue<number>())}</span>,
  },
  { accessorKey: 'method', header: 'วิธีชำระ' },
  { accessorKey: 'status', header: 'สถานะ' },
  {
    accessorKey: 'total',
    header: 'ยอด',
    cell: (c) => <span className="num">{fmtAmount(c.getValue<number>())}</span>,
  },
];
const saleDateFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Bangkok',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});
const saleTimeFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Bangkok',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
const toRow = (t: SaleTxn): SalesRow => ({
  id: t.id,
  branchId: t.branchId,
  receiptNo: t.receiptNo ?? t.id,
  date: `${saleDateFormat.format(t.at)} ${saleTimeFormat.format(t.at).replace(':', '.')}`,
  items: t.items.length,
  method: effectivePaymentLabels(t).join(' + '),
  status: t.status === 'refund' ? 'คืนเงิน' : 'ปกติ',
  total: t.total,
});

const pagedTxnColumns = txnColumns.map((column) => ({
  ...column,
  enableSorting: false,
}));

export function DailySalesView() {
  const queryClient = useQueryClient();
  const { scope, ready } = useBranchScope();
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const receiptColumns = useMemo<ColumnDef<SalesRow, unknown>[]>(() => {
    if (scope.branchId) return pagedTxnColumns;
    const branchNames = new Map(
      branches.map((branch) => [branch.id, branch.short || branch.name]),
    );
    return [
      pagedTxnColumns[0],
      {
        id: 'branch',
        header: 'สาขา',
        enableSorting: false,
        accessorFn: (row) => branchNames.get(row.branchId) ?? row.branchId,
      },
      ...pagedTxnColumns.slice(1),
    ];
  }, [scope.branchId, branches]);
  const { range, prev } = useDateRange();
  const { data, isLoading } = useQuery({
    queryKey: [
      'report',
      'daily',
      scope.branchId,
      scope.allowedBranchIds,
      range.fromDay,
      range.toDay,
    ],
    queryFn: () => getDailyAnalytics(scope, range, prev),
    enabled: ready,
  });
  const [method, setMethod] = useState<MethodFilter>('all');
  const [status, setStatus] = useState<'all' | 'normal' | 'refund'>('all');
  const [pageSize, setPageSize] = useState<SalesPageQuery['pageSize']>(20);
  const pageScope = JSON.stringify([
    scope.branchId,
    scope.allowedBranchIds,
    range.fromDay,
    range.toDay,
    method,
    status,
    pageSize,
  ]);
  const [pagination, setPagination] = useState({ scope: pageScope, page: 1 });
  // Reset synchronously so a changed filter never requests the old page offset.
  const page = pagination.scope === pageScope ? pagination.page : 1;
  if (pagination.scope !== pageScope) {
    setPagination({ scope: pageScope, page: 1 });
  }
  const setPage = (next: number) =>
    setPagination({ scope: pageScope, page: next });
  const {
    data: txnData,
    isLoading: txnsLoading,
    isFetching: txnsFetching,
    isError: txnsError,
    refetch: refetchTxns,
  } = useQuery({
    queryKey: [
      'report',
      'daily-txns',
      page,
      pageSize,
      method,
      status,
      scope.branchId,
      scope.allowedBranchIds,
      range.fromDay,
      range.toDay,
    ],
    queryFn: () =>
      getDailySales(scope, range, { page, pageSize, method, status }),
    enabled: ready,
  });

  const total = data?.total ?? 0;
  const days = data?.days ?? 1;
  const totalDelta = deltaFrac(total, data?.prevTotal ?? 0);

  const dayRows = useMemo<DayRow[]>(
    () =>
      (data?.series ?? []).map((s, i, arr) => ({
        day: s.label,
        revenue: s.value,
        dod: i > 0 ? deltaFrac(s.value, arr[i - 1].value) : null,
        share: total ? s.value / total : 0,
      })),
    [data, total],
  );
  const [dayPageSize, setDayPageSize] =
    useState<SalesPageQuery['pageSize']>(20);
  const dayPageScope = JSON.stringify([
    scope.branchId,
    scope.allowedBranchIds,
    range.fromDay,
    range.toDay,
    dayPageSize,
  ]);
  const [dayPagination, setDayPagination] = useState({
    scope: dayPageScope,
    page: 1,
  });
  const dayPageCount = Math.max(1, Math.ceil(dayRows.length / dayPageSize));
  const dayPage =
    dayPagination.scope === dayPageScope
      ? Math.min(dayPagination.page, dayPageCount)
      : 1;
  if (
    dayPagination.scope !== dayPageScope ||
    (data && dayPagination.page > dayPageCount)
  ) {
    setDayPagination({ scope: dayPageScope, page: dayPage });
  }
  const dayFirstItem = dayRows.length ? (dayPage - 1) * dayPageSize + 1 : 0;
  const dayLastItem = Math.min(dayPage * dayPageSize, dayRows.length);
  const dayExportRows = useMemo<DayExportRow[]>(
    () =>
      dayRows.map((r) => ({
        day: r.day,
        revenueBaht: fmtBaht(r.revenue),
        revenueSatang: r.revenue,
        dodPct: r.dod == null ? '—' : fmtPct(r.dod * 100),
        sharePct: fmtPct(r.share * 100),
      })),
    [dayRows],
  );

  const txnRows = useMemo(() => (txnData?.txns ?? []).map(toRow), [txnData]);
  const txnTotal = txnData?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(txnTotal / pageSize));
  const firstItem = txnRows.length ? (page - 1) * pageSize + 1 : 0;
  const lastItem = txnRows.length ? firstItem + txnRows.length - 1 : 0;

  useEffect(() => {
    if (txnData && !txnsFetching && !txnsError && page > pageCount) {
      // A reduced result set also makes previously cached pages stale.
      // Invalidate without fetching; switching pages loads only the target page.
      void queryClient.invalidateQueries({
        queryKey: ['report', 'daily-txns'],
        refetchType: 'none',
      });
      setPagination({ scope: pageScope, page: pageCount });
    }
  }, [
    txnData,
    txnsFetching,
    txnsError,
    page,
    pageCount,
    pageScope,
    queryClient,
  ]);

  return (
    <div className="bo-fade">
      <ReportHeader
        title="ยอดขายรายวัน"
        subtitle="แนวโน้มรายวัน · รูปแบบรายชั่วโมง · การชำระเงิน และรายการขาย"
        status={{
          label:
            !ready || isLoading
              ? 'กำลังประมวลผล'
              : `${dayRows.length} วันในรายงาน`,
          tone: !ready || isLoading ? 'neutral' : 'ok',
        }}
        actions={
          <ExportMenu
            rows={dayExportRows}
            cols={dayExportCols}
            filename="ยอดขายรายวัน"
            title="ยอดขายรายวัน"
            pdf
          />
        }
      />
      <ReportDateFilter />

      <section aria-labelledby="daily-summary-heading" className="mb-6">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2
              id="daily-summary-heading"
              className="text-h3 font-bold text-ink"
            >
              ภาพรวมช่วงที่เลือก
            </h2>
            <p className="mt-1 text-sm text-ink-3">
              ยอดสุทธิ แนวโน้ม และวันที่ทำยอดสูงสุด
            </p>
          </div>
          <span className="text-sm-label text-ink-3">{days} วัน</span>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <KpiCard
            label="ยอดขายรวม"
            value={fmtBaht(total)}
            tone="primary"
            delta={totalDelta}
            sub="เทียบงวดก่อน"
          />
          <KpiCard
            label="เฉลี่ย/วัน"
            value={fmtBaht(days ? total / days : 0)}
            tone="accent"
            sub={`${days} วัน`}
          />
          <KpiCard
            label="วันขายสูงสุด"
            value={data?.bestDay ? `วันที่ ${data.bestDay.label}` : '—'}
            tone="ok"
            sub={data?.bestDay ? fmtBahtShort(data.bestDay.value) : undefined}
          />
        </div>
      </section>

      <section
        aria-labelledby="daily-insights-heading"
        className="mb-8 space-y-6"
      >
        <div>
          <h2
            id="daily-insights-heading"
            className="text-h3 font-bold text-ink"
          >
            วิเคราะห์ยอดขาย
          </h2>
          <p className="mt-1 text-sm text-ink-3">
            เปรียบเทียบแนวโน้มรายวันและช่องทางรับชำระ
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card title="แนวโน้มยอดขายรายวัน" className="lg:col-span-2">
            <Suspense fallback={chartSkeleton}>
              <BarCompare
                data={data?.series ?? []}
                valueFormat={fmtBahtShort}
                tickFormat={fmtSatangCompact}
              />
            </Suspense>
          </Card>
          <Card title="สัดส่วนการชำระเงิน (บาท)">
            <Suspense fallback={chartSkeleton}>
              <DonutShare
                data={data?.payment ?? []}
                valueFormat={fmtBahtShort}
              />
            </Suspense>
          </Card>
        </div>

        {(data?.byHour?.length ?? 0) > 0 && (
          <Card title="ช่วงเวลาที่ขายดี (เฉลี่ยต่อวัน)">
            <Suspense fallback={chartSkeleton}>
              <BarCompare
                data={data!.byHour}
                valueFormat={fmtBahtShort}
                tickFormat={fmtSatangCompact}
              />
            </Suspense>
          </Card>
        )}
        <section aria-labelledby="daily-breakdown-heading">
          <h3
            id="daily-breakdown-heading"
            className="mb-3 text-h3 font-bold text-ink"
          >
            ยอดขายแยกตามวัน
          </h3>
          {!ready || isLoading ? (
            <div className="bo-card h-40 animate-pulse" />
          ) : (
            <DataTable
              columns={dayColumns}
              data={dayRows}
              pagination={{ pageIndex: dayPage - 1, pageSize: dayPageSize }}
              onPaginationChange={(updater) => {
                const current = {
                  pageIndex: dayPage - 1,
                  pageSize: dayPageSize,
                };
                const next =
                  typeof updater === 'function' ? updater(current) : updater;
                setDayPagination({
                  scope: dayPageScope,
                  page: next.pageIndex + 1,
                });
              }}
              mobileCards
            />
          )}
          <nav
            aria-label="แบ่งหน้ายอดขายรายวัน"
            className="mt-4 flex flex-wrap items-center justify-between gap-3"
          >
            <label className="flex items-center gap-2 text-sm-label text-ink-2">
              วันต่อหน้า
              <Select
                size="sm"
                aria-label="วันต่อหน้า"
                value={dayPageSize}
                onChange={(event) =>
                  setDayPageSize(
                    Number(event.target.value) as SalesPageQuery['pageSize'],
                  )
                }
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Select>
            </label>
            <span aria-live="polite" className="text-sm-label text-ink-3">
              {ready && !isLoading
                ? `แสดง ${fmtInt(dayFirstItem)}–${fmtInt(dayLastItem)} จาก ${fmtInt(dayRows.length)} วัน`
                : '—'}
            </span>
            <div className="flex items-center gap-3">
              <Btn
                disabled={dayPage <= 1 || !ready || isLoading}
                onClick={() =>
                  setDayPagination({ scope: dayPageScope, page: dayPage - 1 })
                }
              >
                ก่อนหน้า
              </Btn>
              <span className="text-sm-label text-ink-3">
                หน้า {dayPage} / {dayPageCount}
              </span>
              <Btn
                disabled={dayPage >= dayPageCount || !ready || isLoading}
                onClick={() =>
                  setDayPagination({ scope: dayPageScope, page: dayPage + 1 })
                }
              >
                ถัดไป
              </Btn>
            </div>
          </nav>
        </section>
      </section>
      <section aria-labelledby="recent-receipts-heading" className="mb-6">
        <div className="mb-3 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h2
              id="recent-receipts-heading"
              className="text-h3 font-bold text-ink"
            >
              บิลล่าสุด
            </h2>
            <p className="mt-1 text-sm-label text-ink-3">
              บิลในช่วงที่เลือก เรียงจากใหม่สุด
            </p>
          </div>
          <div
            role="group"
            aria-label="ตัวกรองบิลล่าสุด"
            className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-center sm:gap-4"
          >
            <label className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
              <span className="shrink-0 text-sm-label text-ink-3">
                วิธีชำระ
              </span>
              <Select
                size="sm"
                wrapperClassName="min-w-0 sm:w-48"
                aria-label="วิธีชำระ"
                value={method}
                onChange={(e) => setMethod(e.target.value as MethodFilter)}
              >
                <option value="all">ทั้งหมด</option>
                <option value="cash">เงินสด</option>
                <option value="qr_gateway">QR นิติ-SCB</option>
                <option value="qr_personal">พร้อมเพย์ส่วนบุคคล</option>
                <option value="reference_manual">
                  {LEGACY_MANUAL_PAYMENT_LABEL}
                </option>
              </Select>
            </label>
            <label className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
              <span className="shrink-0 text-sm-label text-ink-3">สถานะ</span>
              <Select
                size="sm"
                wrapperClassName="min-w-0 sm:w-28"
                aria-label="สถานะ"
                value={status}
                onChange={(e) =>
                  setStatus(e.target.value as 'all' | 'normal' | 'refund')
                }
              >
                <option value="all">ทั้งหมด</option>
                <option value="normal">ปกติ</option>
                <option value="refund">คืนเงิน</option>
              </Select>
            </label>
          </div>
        </div>
        <div aria-busy={!ready || txnsFetching}>
          {txnsError ? (
            <div role="alert" className="bo-card p-4 text-danger">
              โหลดรายการบิลไม่สำเร็จ
              <Btn className="ml-3" onClick={() => void refetchTxns()}>
                ลองอีกครั้ง
              </Btn>
            </div>
          ) : !ready || txnsLoading ? (
            <div role="status" className="bo-card p-6 text-ink-3">
              กำลังโหลดบิล...
            </div>
          ) : (
            <DataTable
              key={pageSize}
              pageSize={pageSize}
              columns={receiptColumns}
              data={txnRows}
              emptyMessage="ไม่พบบิลในช่วงเวลาที่เลือก"
              mobileCards
            />
          )}
        </div>
        <nav
          aria-label="แบ่งหน้าบิลล่าสุด"
          className="mt-4 flex flex-wrap items-center justify-between gap-3"
        >
          <label className="flex items-center gap-2 text-sm-label text-ink-2">
            รายการต่อหน้า
            <Select
              aria-label="รายการต่อหน้า"
              value={pageSize}
              onChange={(event) =>
                setPageSize(
                  Number(event.target.value) as SalesPageQuery['pageSize'],
                )
              }
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Select>
          </label>
          <span aria-live="polite" className="text-sm-label text-ink-3">
            {txnData && !txnsError
              ? `รายการ ${fmtInt(firstItem)}–${fmtInt(lastItem)} จาก ${fmtInt(txnTotal)} รายการ`
              : '—'}
          </span>
          <div className="flex items-center gap-3">
            <Btn
              disabled={page <= 1 || txnsFetching || !ready}
              onClick={() => setPage(page - 1)}
            >
              ก่อนหน้า
            </Btn>
            <span className="text-sm-label text-ink-3">
              หน้า {page} / {pageCount}
            </span>
            <Btn
              disabled={
                !txnData?.hasMore || txnsFetching || txnsError || !ready
              }
              onClick={() => setPage(page + 1)}
            >
              ถัดไป
            </Btn>
          </div>
        </nav>
      </section>
    </div>
  );
}
